import type { CountryCode } from 'libphonenumber-js';
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import { routing } from '@/libs/i18nNavigation';

export const getBaseUrl = () => {
  if (process.env.NEXT_PUBLIC_APP_URL) {
    return process.env.NEXT_PUBLIC_APP_URL;
  }

  if (
    process.env.VERCEL_ENV === 'production' &&
    process.env.VERCEL_PROJECT_PRODUCTION_URL
  ) {
    return `https://${process.env.VERCEL_PROJECT_PRODUCTION_URL}`;
  }

  if (process.env.VERCEL_URL) {
    return `https://${process.env.VERCEL_URL}`;
  }

  return 'http://localhost:3000';
};

export const getI18nPath = (url: string, locale: string) => {
  if (locale === routing.defaultLocale) {
    return url;
  }

  return `/${locale}${url}`;
};

/**
 * Format currency amounts using proper Intl.NumberFormat
 * @param value - The numeric value to format
 * @param currency - The currency code (defaults to 'USD')
 * @param locale - The locale for formatting (defaults to 'en-US')
 * @returns Formatted currency string (e.g., "$1,234.56")
 */
export const formatCurrency = (
  value: number,
  currency = 'USD',
  locale = 'en-US'
): string => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

/**
 * Clean and normalize phone number for API submission
 * @param value - The formatted phone number string
 * @returns Clean phone number in E.164 format or original value if invalid
 */
export const cleanPhoneNumber = (value: string): string => {
  if (!value) {
    return '';
  }

  try {
    // Parse the phone number with US as default country
    const phoneNumber = parsePhoneNumber(value, 'US');

    // Return E.164 format if valid, otherwise return original
    return phoneNumber ? phoneNumber.format('E.164') : value;
  } catch {
    // If parsing fails, return the original value
    return value;
  }
};

/**
 * Validate if a phone number is valid
 * @param value - The phone number string to validate
 * @param country - The default country code (defaults to 'US')
 * @returns Boolean indicating if the phone number is valid
 */
export const isValidPhone = (
  value: string,
  country: CountryCode = 'US'
): boolean => {
  if (!value) {
    return false;
  }

  try {
    return isValidPhoneNumber(value, country);
  } catch {
    return false;
  }
};

/**
 * Format a phone number for display purposes
 * @param value - The phone number string to format
 * @param formatType - The format type ('national' | 'international' | 'e164')
 * @param country - The default country code (defaults to 'US')
 * @returns Formatted phone number string
 */
export const displayPhoneNumber = (
  value: string,
  formatType: 'national' | 'international' | 'e164' = 'national',
  country: CountryCode = 'US'
): string => {
  if (!value) {
    return '';
  }

  try {
    const phoneNumber = parsePhoneNumber(value, country);

    if (!phoneNumber) {
      return value;
    }

    switch (formatType) {
      case 'international':
        return phoneNumber.formatInternational();
      case 'e164':
        return phoneNumber.format('E.164');
      default:
        return phoneNumber.formatNational();
    }
  } catch {
    return value;
  }
};

/**
 * Format a currency value for display purposes
 * @param value - The currency value string to format
 * @returns Formatted currency string (e.g., "$1,234.56")
 */
export const formatCurrencyLocal = (value: string): string => {
  // Remove all non-digit characters
  const digitsOnly = value.replace(/\D/g, '');
  if (!digitsOnly) {
    return '';
  }
  // Convert to number and format as currency
  const num = Number.parseInt(digitsOnly, 10);
  const formatted = (num / 100).toFixed(2);
  return formatted;
};
